// Where a record of each audited type lives in the admin. Only types with a
// page of their own appear; anything else is text. Every path here is a
// route that exists in src/layout (checked when this was written).
const PAGES = {
  users: id => `/org/user/list/edit/${id}`,
  groups: id => `/org/group/list/edit/${id}`,
  trade_shows: id => `/org/tradeshow/list/edit/${id}`,
  self_registers: id => `/org/self-register/list/edit/${id}`,
  organizations: id => `/admin/organization/list/edit/${id}`,
  providers: id => `/admin/sso/connections/${id}`,
  logins: id => `/admin/login/list/edit/${id}`,
  apps: id => `/admin/app/edit/${id}`,
  role_sys: id => `/root/role-sys/list/edit/${id}`,
  // AI tools telemetry (R2): a session's and a postmortem's staff page
  ai_tools_sessions: id => `/admin/ai-tools/sessions/${id}`,
  ai_tools_postmortems: id => `/admin/ai-tools/postmortems/${id}`
}

const RECYCLE = {
  users: '/org/user/list/recycle',
  groups: '/org/group/list/recycle',
  trade_shows: '/org/tradeshow/list/recycle',
  self_registers: '/org/self-register/list/recycle',
  organizations: '/admin/organization/list/recycle',
  providers: '/admin/sso/connections/recycle',
  logins: '/admin/login/list/recycle',
  apps: '/admin/app/recycle',
  ai_tools_sessions: '/admin/ai-tools/recycle?tab=sessions',
  ai_tools_postmortems: '/admin/ai-tools/recycle?tab=postmortems'
}

// What a record of a type is called when content names none (a table the
// trail doesn't capture has no catalogue label): "AI tools session 123",
// never "Record 123". The AI tools telemetry rows staff move to the recycle
// bin, restore, delete for good and mark (ai_tools.record_* events).
const NOUNS = {
  ai_tools_sessions: 'AI tools session',
  ai_tools_events: 'AI tools record',
  ai_tools_postmortems: 'AI tools postmortem',
  ai_tools_findings: 'AI tools finding'
}
export const recordNoun = type => NOUNS[type] || null

// The record types with a page (record-routes.registry.test.js checks each
// is a type the trail records).
export const LINKED_RECORD_TYPES = [...new Set([...Object.keys(PAGES), ...Object.keys(RECYCLE)])]

export const recordHref = (type, id) => (PAGES[type] && id ? PAGES[type](encodeURIComponent(id)) : null)

export const recycleHref = type => RECYCLE[type] || null

// A course's page. Content nodes are courses, templates and categories, and
// a Ref doesn't say which, so only a caller that knows its node is a course
// (a course launch) links here (recordView's course option).
export const courseHref = id => (id ? `/org/node-content/course/${encodeURIComponent(id)}` : null)
export const COURSE_RECYCLE = '/org/node-content/recycle'

// A course, template, library or folder by its node type (content's
// gc_datatype, an entry's context): its page in its section, or null for
// a type with no page of its own (a bundle). Each section's routes take
// /course|template|library/:id and /node/:id from any depth, and once the
// section's list has loaded they move such an address under the node's
// folders, so its trail names them (node/common/shared/nested-path.js,
// audit F3). A link here needs only the node's id.
const NODE_PAGES = {
  COURSE: ['/org/node-content', 'course'],
  STREAMING_VIDEO: ['/org/node-content', 'course'],
  CATEGORY: ['/org/node-content', 'node'],
  COURSE_TEMPLATE: ['/org/node-template', 'template'],
  COURSE_TEMPLATE_CATEGORY: ['/org/node-template', 'node'],
  NODE_LIBRARY: ['/org/node-library', 'library'],
  NODE_LIBRARY_CATEGORY: ['/org/node-library', 'node']
}
export const nodeHref = (type, id) => {
  const page = NODE_PAGES[String(type || '').toUpperCase()]
  return page && id ? `${page[0]}/${page[1]}/${encodeURIComponent(id)}` : null
}

// A file (an asset) of a node, and one of its versions (owner 2026-10-09:
// a story's "macOS build" opens the file, its "v4" that version): a file's
// versions are under its node's page, at the node's "File versions" (its
// ⋮ menu, /edit/publishes), and a version's own page is under those. A
// file on a bundle links under the course above it: these pages read the
// file by its id. null for a node with no page of its own, or a missing id.
export const assetHref = (nodeType, nodeID, assetID) => {
  const node = nodeHref(nodeType, nodeID)
  return node && assetID ? `${node}/edit/publishes/${encodeURIComponent(assetID)}/version` : null
}
export const assetVersionHref = (nodeType, nodeID, assetID, versionID) => {
  const asset = assetHref(nodeType, nodeID, assetID)
  return asset && versionID ? `${asset}/${encodeURIComponent(versionID)}/edit` : null
}

// A person's page: super admins open the login on the admin side; everyone
// else opens the member in their organization.
export const personHref = (actor, sys) => {
  if (!actor) return null
  if (sys && actor.loginID) return `/admin/login/list/edit/${encodeURIComponent(actor.loginID)}`
  return actor.userID ? `/org/user/list/edit/${encodeURIComponent(actor.userID)}` : null
}

// An audit entry's permalink: the org audit log, or the super admin's.
export const entryPath = (id, sys) => `${sys ? '/admin' : '/org'}/audit-log/entry/${encodeURIComponent(id)}`
